import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import type { Theme } from 'webapp/enums/enums';
import { AttachPosition, Direction, PopupEvent } from 'webapp/enums/enums';
import { isElementVisible } from 'webapp/utils/html';

import themed from 'webapp/decorators/themed';
import BlockPopupPreviewPropsComponent from 'webapp/components/block/popup-preview-props';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormRangePopupComponent from 'webapp/components/form/range/popup';
import ScrollableTextComponent from 'webapp/components/scrollable-text';

import DateRange from 'webapp/models-custom/date-range';
import type DsCompare from 'webapp/models-custom/ds-compare';
import type MetricLimitations from 'webapp/models-custom/metric-limitations';

export enum AttachToOption {
	NODE = 'node',
	TEXT = 'text',
	ICON = 'icon',
}

export interface Args {
	metricLimitations: MetricLimitations;
	onChange: (dateRange: DateRange, compare?: DsCompare, applyToWholeLoop?: boolean) => void;
	theme?: Theme | string;
	size?: string;
	isDisabled?: boolean;
	exceptionIds?: number[];
	addEm?: boolean;
	dateText?: string;
	labelText?: string;
	openAllBlockPreviewProps?: boolean;
	attachTo?: AttachToOption;
	onPopupOpenStateChange?: (isOpen: boolean) => void;
	buttonTheme?: string; // for block/popup-preview-props
	switchTheme?: string; // for block/popup-preview-props
	trackingOrigin?: string; // for block/popup-preview-props
	collapseGranulation?: boolean; // for block/popup-preview-props
	footerCheckboxLabel?: string; // for block/popup-preview-props
	showMasterCompare?: boolean; // for block/popup-preview-props
	showScrollableLabel?: boolean;
	popupDirection?: Direction[];
	iconSize?: IconSize;
	hideIcon?: boolean;
}

@themed()
export default class FormRangeInlineComponent<T extends Args> extends Component<T> {
	@service('popup') declare popupService: Service.Popup;

	declare size: string; // from @themed
	declare sizeClass: string; // from @themed
	declare theme: string; // from @themed
	declare themeClass: string; // from @themed

	@tracked isLoading = false;
	@tracked isPopupOpen = false;

	get dateRange() {
		return this.args.metricLimitations.dateRange;
	}

	get showAllTime() {
		return true;
	}

	get exceptionIds() {
		// won't be showing these categories in the popup
		return this.args.exceptionIds || [];
	}

	get labelText() {
		return this.args.labelText ?? 'Date range';
	}

	get showDateText() {
		return this.args.dateText && !this.dateRange?.isCustom;
	}

	get iconSize(): IconSize {
		return this.args.iconSize ?? 'dynamic';
	}

	get showIcon(): boolean {
		return !this.args.hideIcon;
	}

	@action
	openDropdown(e: PointerEvent) {
		const selectorCt = (e.target as HTMLElement)?.closest<HTMLElement>('.range-selector');
		let attachTo: HTMLElement | null | undefined = null;

		switch (this.args.attachTo) {
			case AttachToOption.NODE:
				attachTo = (e.target as HTMLElement)?.closest<HTMLElement>('.cdr-range-inline');
				break;
			case AttachToOption.TEXT:
				attachTo = selectorCt?.querySelector('span.date');
				break;
			case AttachToOption.ICON:
			default:
				attachTo = (selectorCt?.querySelector('ui-icon') ??
					selectorCt?.querySelector('span.icon'))!;
				break;
		}

		if (this.args.isDisabled || this.isPopupOpen || !attachTo || !isElementVisible(attachTo)) {
			return;
		}

		const component = this.args.openAllBlockPreviewProps
			? BlockPopupPreviewPropsComponent
			: FormRangePopupComponent;

		const props = this.args.openAllBlockPreviewProps
			? this.getBlockPreviewPopupProps()
			: this.getFormRangePopupProps();

		const popupClass = this.args.openAllBlockPreviewProps
			? 'popup-block-preview-props'
			: 'popup-as-dropdown popup-cdr popup-cdr-range popup-cdr-inline';

		const allowStacked = !!this.args.openAllBlockPreviewProps;

		const popup = this.popupService.createContext(component, attachTo, {
			popupClass,
			directions: this.args.popupDirection ?? [Direction.DOWN, Direction.UP],
			theme: this.theme,
			allowStacked,
			attachPosition: AttachPosition.START,
			alignEdgeWithAttachTo: true,
			hideArrow: true,
		});

		popup.on(PopupEvent.OPENING, this, () => {
			this.isPopupOpen = true;
		});

		popup.open(props);

		popup.on(PopupEvent.CLOSING, this, () => {
			this.isPopupOpen = false;
			if (this.args.onPopupOpenStateChange) {
				this.args.onPopupOpenStateChange(false);
			}
		});

		if (this.args.onPopupOpenStateChange) {
			this.args.onPopupOpenStateChange(true);
		}

		this.isLoading = true;
		this.args.metricLimitations.updateLimitations();
		void this.args.metricLimitations.limitationsLoaded?.then(() => {
			this.isLoading = false;
		});
	}

	private getFormRangePopupProps() {
		const popupData = Object.assign({}, this.getCommonPopupData());
		return popupData;
	}

	private getBlockPreviewPopupProps() {
		const popupData: PopupData.Block.PreviewProps = Object.assign(
			{},
			this.getCommonPopupData(),
		);

		popupData.collapseGranulation = this.args.collapseGranulation;
		popupData.footerCheckboxLabel = this.args.footerCheckboxLabel;
		popupData.buttonTheme = this.args.buttonTheme;
		popupData.switchTheme = this.args.switchTheme;
		popupData.trackingOrigin = this.args.trackingOrigin;
		popupData.showMasterCompare = this.args.showMasterCompare;

		return popupData;
	}

	private getCommonPopupData() {
		const exceptionIds = Array.from(this.exceptionIds);

		if (!exceptionIds.includes(DateRange.Category.POPULAR) || !this.showAllTime) {
			// all time is already shown in the popular section
			if (!exceptionIds.includes(DateRange.Category.ALLTIME)) {
				exceptionIds.push(DateRange.Category.ALLTIME);
			}
		}

		const props: PopupData.Form.Range = {
			metricLimitations: this.args.metricLimitations,
			exceptionIds,
			showAllTime: this.showAllTime,
			onChange: this.args.onChange,
		};

		return props;
	}
	<template>
		<div
			class="cdr-range-inline
				{{if @isDisabled 'disabled'}}
				{{if this.isPopupOpen 'cdr-range-inline--open'}}
				{{this.themeClass}}
				{{this.sizeClass}}
				{{if @showScrollableLabel 'scrollable'}}"
		>
			{{#if this.labelText}}
				<span class="range-label">{{this.labelText}}</span>
			{{/if}}

			<a
				class="range-selector {{if @addEm 'em'}} {{if this.isLoading 'loading'}}"
				{{on "click" this.openDropdown}}
			>
				<span class="date">

					{{#if @showScrollableLabel}}
						<ScrollableTextComponent>
							{{this.dateRange.rangeTitleUX}}
						</ScrollableTextComponent>
					{{else}}
						{{this.dateRange.rangeTitleUX}}
					{{/if}}

					{{#if this.showDateText}}
						{{@dateText}}
					{{/if}}
				</span>
				{{#if this.showIcon}}
					<CommonUiIconComponent @name="ChevronDown" @size={{this.iconSize}} />
				{{/if}}
			</a>
		</div>
	</template>
}
